import * as React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';
import { h400 } from '@helpcenter/styles/src/fonts';
import { ThemedButton } from '@helpcenter/themed-button';
import { ThemedLink } from '@helpcenter/themed-link';
import type { WrappedComponentProps } from 'react-intl';
import { FormattedMessage, injectIntl } from 'react-intl';
import { TextField } from 'view/common/fields/text-field';
import fg from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import { Box, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { UseTypographyMigratedComponent } from '@atlassian/help-center-common-util/enable-typography-migration';
import messages from './messages';

interface LoginSingleSignOnProps {
    email: string;
    redirectUrl?: string;
}

interface LoginSingleSignOnState {
    isInIframe: boolean;
}

export class LoginSingleSignOn extends React.Component<
    LoginSingleSignOnProps & WrappedComponentProps,
    LoginSingleSignOnState
> {
    state: LoginSingleSignOnState = {
        isInIframe: false,
    };

    componentDidMount() {
        this.setState({
            isInIframe: window.self !== window.top,
        });
    }

    getSSOUrl = () => {
        return this.props.redirectUrl;
    };

    getSSOFormContent = () => {
        const { intl, email } = this.props;

        return (
            <TextField
                id="user-email"
                shouldFitContainer
                label={intl.formatMessage(messages.emailLabel)}
                disabled
                defaultValue={email}
            />
        );
    };

    getAtlassianAccountInfoSection = () => {
        const { intl } = this.props;

        const URI = 'https://support.atlassian.com/atlassian-account/docs/what-is-an-atlassian-account/';

        const supportLink = (
            <ThemedLink actionSubjectId="learnMoreAtlassianAccount" href={URI} target="_blank">
                {intl.formatMessage(messages.atlassianAccountInfoLink)}
            </ThemedLink>
        );

        return (
            <InfoSectionContainer>
                <UseTypographyMigratedComponent
                    migratedComponent={
                        <Box paddingBlockStart="space.300">
                            <Heading size="xsmall">
                                <FormattedMessage {...messages.atlassianAccountInfoTitle} />
                            </Heading>
                        </Box>
                    }
                >
                    {/* a11y: A11Y-28843 — InfoSectionTitle is a styled div with no heading semantics */}
                    <InfoSectionTitle
                        role={fg.checkGate('ai-a11y-131-28903-28906') ? 'heading' : undefined}
                        aria-level={fg.checkGate('ai-a11y-131-28903-28906') ? 3 : undefined}
                    >
                        <FormattedMessage {...messages.atlassianAccountInfoTitle} />
                    </InfoSectionTitle>
                </UseTypographyMigratedComponent>
                <UseTypographyMigratedComponent
                    migratedComponent={
                        <Box paddingBlockStart="space.050">
                            <Text>
                                {fg.checkGate('jsm_rename_product_to_app') ? (
                                    <FormattedMessage
                                        {...messages.atlassianAccountInfoContentAppify}
                                        values={{ supportLink }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        {...messages.atlassianAccountInfoContent}
                                        values={{ supportLink }}
                                    />
                                )}
                            </Text>
                        </Box>
                    }
                >
                    <InfoSectionContent>
                        {fg.checkGate('jsm_rename_product_to_app') ? (
                            <FormattedMessage
                                {...messages.atlassianAccountInfoContentAppify}
                                values={{ supportLink }}
                            />
                        ) : (
                            <FormattedMessage {...messages.atlassianAccountInfoContent} values={{ supportLink }} />
                        )}
                    </InfoSectionContent>
                </UseTypographyMigratedComponent>
            </InfoSectionContainer>
        );
    };

    render() {
        return (
            <Container>
                <FireScreenEventOnMount />
                {this.getSSOFormContent()}
                <ButtonContainer>
                    <ThemedButton
                        id="login-button"
                        data-test-id="login-sso-button"
                        appearance="primary"
                        shouldFitContainer
                        actionSubjectId="submitLoginSSO"
                        href={this.getSSOUrl()}
                        target={this.state.isInIframe && isJsmPortalRovoIntegrationEnabled() ? '_blank' : undefined}
                        rel={this.state.isInIframe && isJsmPortalRovoIntegrationEnabled() ? 'opener' : undefined}
                        autoFocus
                    >
                        <FormattedMessage {...messages.continueWithAtlassianAccountButton} />
                    </ThemedButton>
                </ButtonContainer>
                {this.getAtlassianAccountInfoSection()}
            </Container>
        );
    }
}

export default injectIntl(LoginSingleSignOn);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div`
    text-align: left;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ButtonContainer = styled.div`
    margin-top: ${token('space.300', '24px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const InfoSectionContainer = styled.div`
    margin-top: ${token('space.300', '24px')};
    border-top: ${token('border.width', '1px')} solid ${token('color.border', 'rgba(9, 30, 66, 0.14)')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const InfoSectionTitle = styled.div`
    ${h400};
    margin-top: ${token('space.300', '24px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const InfoSectionContent = styled.div`
    margin-top: ${token('space.050', '4px')};
`;
